/**
 * "Was this draft useful?" — thumbs on a drafted reply.
 *
 * Per DRAFT rather than per tab, because "the tab is bad" cannot be acted on while "model drafts for
 * process questions get thumbs down" names the prompt to fix. The vote travels with the category and
 * the draft source for exactly that reason.
 *
 * Three deliberate details:
 *  - a thumbs DOWN asks what was wrong, and will not submit without it: a down-vote with no words is
 *    a mood, not a signal;
 *  - the comment goes to our own store and NEVER to analytics — a recruiter writing about a
 *    candidate's thread can name them (see the PII rule in CLAUDE.md);
 *  - a failure is swallowed. This is telemetry in the middle of someone's reply, and a lost vote must
 *    not look like a broken button.
 */
import { useState } from 'react';
import { Button } from '@finn-auto/design-system/atoms/button';
import { Input } from '@finn-auto/design-system/atoms/input';
import * as api from '../../services/taActionBoardApi';
import { logAnalyticsEvent } from '../../../../analytics/nativeAnalytics';
import type { InboundRequest } from '../../types/taActionBoard';

export function DraftFeedback({ item, tab = 'requests' }: { item: InboundRequest; tab?: string }) {
  const [state, setState] = useState<'idle' | 'commenting' | 'done'>('idle');
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);

  const send = async (vote: 'up' | 'down', text?: string) => {
    setBusy(true);
    try {
      await api.rateDraft({
        threadId: item.threadId,
        messageId: item.messageId,
        vote,
        comment: text,
        category: item.category,
        draftSource: item.draftSource,
        tab,
        /**
         * The mail and the draft travel WITH the vote, so the Usage board can show what the verdict
         * was about: "wrong tone" is unusable on its own. `questionFull` rather than `question` —
         * the collapsed card truncates, and a sample that cuts off the actual question is worse
         * than none. `item.draft` is the text as GENERATED, not the editor's current content: what
         * is being rated is our output, not the recruiter's rewrite of it.
         */
        subject: item.subject,
        candidateText: item.questionFull || item.question,
        draftText: item.draft,
      });
      /**
       * Vote, category, source and tab. Never the comment, never the mail, never the draft — the
       * event stream is a different sink with different rules, and this is the line the PII rule
       * draws. The texts live only in the permission-gated store.
       */
      void logAnalyticsEvent('ta_requests_draft_rated', {
        vote,
        category: item.category,
        draftSource: item.draftSource,
        tab,
      });
      setState('done');
    } catch {
      // Telemetry: say thank you anyway rather than blame the recruiter for our outage.
      setState('done');
    } finally {
      setBusy(false);
    }
  };

  if (state === 'done') {
    return <p className="text-xs text-gray-500">Thanks — noted.</p>;
  }

  if (state === 'commenting') {
    return (
      <div className="flex flex-wrap items-end gap-2" data-no-toggle>
        <Input
          label="What was wrong with it?"
          value={comment}
          onChange={(e) => setComment((e.target as HTMLInputElement).value)}
          disabled={busy}
          className="w-64"
        />
        <Button
          size="sm"
          variant="outline"
          disabled={busy || comment.trim().length < 3}
          onClick={() => void send('down', comment)}
        >
          Send
        </Button>
        <Button size="sm" variant="ghost" disabled={busy} onClick={() => setState('idle')}>
          Cancel
        </Button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2 text-xs text-gray-500" data-no-toggle>
      <span>Was this draft useful?</span>
      <Button
        size="sm"
        variant="ghost"
        aria-label="This draft was useful"
        disabled={busy}
        onClick={() => void send('up')}
      >
        👍
      </Button>
      <Button
        size="sm"
        variant="ghost"
        aria-label="This draft was not useful"
        disabled={busy}
        onClick={() => setState('commenting')}
      >
        👎
      </Button>
    </div>
  );
}
